import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useEffect } from 'react';
import { NAME_ATTRIBUTE, RENDER_EVENT, UNMOUNT_EVENT } from './constants';

/**
 * It takes an identifier (`name`) and a component (`component`) and returns a new component which,
 * when rendered, can be found by `useDataAttributes` hook.
 *
 * To work properly, the component taken as parameter must pass over all properties to the element that will be rendered.
 *
 * @example
 * // Correct
 * const Button = withDataAttributes(
 *   'Button',
 *   ({ label, ..rest }) => <button {...rest}>{label}</button>
 * );
 *
 * @example
 * // Incorrect
 * const Button = withDataAttributes(
 *   'Button',
 *   ({ label }) => <button>{label}</button> // Doesn't work
 * );
 *
 * @param {string} name - Identifier which will be associated with the `Component`.
 * After the execution, this parameter will be assigned to `Component` `displayName` property.
 * @param {React.Component} Component - Component that will receive identification attributes when rendered.
 * @returns {React.Component} New component created from `Component` parameter ready to render identification attributes.
 */
export default function withCrossAppID(name, Component) {
  const WithDataAttributes = props => {
    useEffect(() => {
      const event = new global.CustomEvent(RENDER_EVENT, {
        detail: {
          name
        }
      });
      const unmountEvent = new global.CustomEvent(UNMOUNT_EVENT, {
        detail: {
          name
        }
      });
      global.document.dispatchEvent(event);
      return () => global.document.dispatchEvent(unmountEvent);
    }, []);
    return /*#__PURE__*/React.createElement(Component, _extends({}, props, {
      [NAME_ATTRIBUTE]: name
    }));
  };
  WithDataAttributes.displayName = name;
  return WithDataAttributes;
}